import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@contexts/ShopCartContext';
import { useAuth } from 'react-oidc-context';
import { storeService } from '@services/storeService';
import { makeStyles } from '@fluentui/react-components';
import { Dismiss24Regular, Delete16Regular } from '@fluentui/react-icons';
import AlyzeWhiteLogo from '@/assets/AlyzeWhiteLogo.svg';
import { StoreNavSidebar } from './StoreNavSidebar.tsx';
import { AlyzeUserLogin } from '@components/shared/AlyzeUserLogin';

const useStyles = makeStyles({
    navigation: {
        position: 'sticky',
        top: '0',
        zIndex: 1000,
        backgroundColor: 'rgba(34, 34, 34, 0.32)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #F4F0E7',
        width: '100%',
        maxWidth: '100vw',
        height: '70px',
        overflow: 'visible',
    },

    desktopNav: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        paddingLeft: '24px',
        paddingRight: '24px',
        '@media (max-width: 768px)': {
            display: 'none',
        },
    },

    navLeft: {
        overflow: 'hidden',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '20px',
        display: 'flex',
    },

    logo: {
        width: '153px',
        height: '30px',
        position: 'relative',
        overflow: 'hidden',
    },

    navRight: {
        height: '68px',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '20px',
        display: 'flex',
    },

    navIcons: {
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '20px',
        display: 'flex',
    },

    cartWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },

    cartButton: {
        backgroundColor: 'transparent',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '0',
        outline: 'none',
        '&:hover': {
            opacity: '0.8',
        },
        '&:focus': {
            outline: 'none',
        },
    },

    cartIcon: {
        width: '24.10px',
        height: '24.08px',
        fill: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',

    },

    cartCount: {
        position: 'absolute',
        top: '-8px',
        right: '-8px',
        backgroundColor: '#D72B00',
        color: 'white',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 'bold',
    },

    refreshCacheButton: {
        backgroundColor: '#C6A972',
        color: '#222222',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: '#6b4a04',
        },
        '&:disabled': {
            backgroundColor: '#ccc',
            cursor: 'not-allowed',
        },
    },

    membershipButton: {
        paddingLeft: '20px',
        paddingRight: '20px',
        paddingTop: '14px',
        paddingBottom: '14px',
        backgroundColor: '#C6A972',
        borderRadius: '40px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        display: 'inline-flex',
        textDecoration: 'none',
        cursor: 'pointer',
        alignSelf: 'center',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': {
            backgroundColor: '#B8966A',
        },
    },

    membershipText: {
        color: '#222222',
        fontSize: '14px',
        fontFamily: 'Figtree',
        fontWeight: '700',
        lineHeight: '14px',
        wordWrap: 'break-word',
    },

    miniCart: {
        position: 'absolute',
        top: '100%',
        right: '0',
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        width: '320px',
        maxHeight: '400px',
        overflowY: 'auto',
        zIndex: 9999,
    },

    miniCartHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        borderBottom: '1px solid #eee',
    },

    miniCartTitle: {
        margin: '0',
        fontSize: '18px',
        fontWeight: '600',
        color: '#333',
    },

    closeButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        color: '#666',
        '&:hover': {
            color: '#333',
        },
    },

    miniCartItems: {
        maxHeight: '250px',
        overflowY: 'auto',
    },

    miniCartItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #eee',
        gap: '12px',
    },

    itemImage: {
        width: '50px',
        height: '50px',
        objectFit: 'cover',
        borderRadius: '4px',
    },

    itemDetails: {
        flex: '1',
    },

    itemName: {
        margin: '0 0 4px 0',
        fontSize: '14px',
        fontWeight: '500',
        color: '#986F0B',
    },

    itemPrice: {
        margin: '0',
        fontSize: '12px',
        color: '#666',
    },

    removeButton: {
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#222222',
        '&:hover': {
            opacity: 0.7,
        },
    },

    emptyCart: {
        padding: '32px 16px',
        textAlign: 'center',
        color: '#666',
        fontSize: '14px',
    },

    miniCartFooter: {
        padding: '16px',
        borderTop: '1px solid #eee',
        backgroundColor: '#f9f9f9',
    },

    cartTotal: {
        fontWeight: '600',
        marginBottom: '12px',
        fontSize: '16px',
        color: '#333',
    },

    viewCartButton: {
        width: '100%',
        backgroundColor: '#242424',
        color: 'white',
        border: 'none',
        padding: '6px 12px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        textDecoration: 'none',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        '&:hover': {
            backgroundColor: '#333333',
        },
    },

    // Mobile Navigation
    mobileNav: {
        display: 'none',
        '@media (max-width: 768px)': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 16px',
        },
    },
});

export const StoreNavHeaderV1: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const auth = useAuth();

    const { items, itemCount, totalAmount, removeItem } = useCart();
    const [showMiniCart, setShowMiniCart] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [hasActiveMembership, setHasActiveMembership] = useState(false);
    const [isCheckingMembership, setIsCheckingMembership] = useState(false);
    const [isRefreshingCache, setIsRefreshingCache] = useState(false);
    const cartRef = useRef<HTMLDivElement>(null);

    // Check if user is admin
    const isAdmin = auth.isAuthenticated && 
        (auth.user?.profile?.['cognito:groups'] as string[] || []).includes('Admin');

    // Check for active membership when user is logged in
    useEffect(() => {
        const checkMembership = async () => {
            if (!auth.isAuthenticated || !auth.user?.profile?.email) {
                setHasActiveMembership(false);
                setIsCheckingMembership(false);
                return;
            }

            setIsCheckingMembership(true);
            try {
                const memberships = await storeService.getActiveMembership(auth.user.profile.email);
                setHasActiveMembership(memberships && memberships.length > 0 && memberships.some(m => m.hasActiveMembership));
            } catch (error) {
                console.error('Error checking membership status:', error);
                setHasActiveMembership(false);
            } finally {
                setIsCheckingMembership(false);
            }
        };

        checkMembership();
    }, [auth.isAuthenticated, auth.user?.profile?.email]);

    // Close mini cart when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (cartRef.current && !cartRef.current.contains(event.target as Node)) {
                setShowMiniCart(false);
            }
        };

        if (showMiniCart) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showMiniCart]);

    const toggleMiniCart = () => {
        setShowMiniCart(!showMiniCart);
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    const handleRemoveItem = (productId: number, isSubscription: boolean = false, selectedInterval?: any) => {
        removeItem(productId, isSubscription, selectedInterval);
    };

    const handleViewCart = () => {
        setShowMiniCart(false);
        navigate('/v1/shop/cart');
    };

    const handleMembershipButtonClick = (e: React.MouseEvent) => {
        if (!auth.isAuthenticated || !hasActiveMembership) {
            e.preventDefault();
            navigate('/v1/memberships');
        }
        // If user is logged in and has membership, let the anchor tag handle navigation to external URL
    };

    const handleRefreshCache = async () => {
        if (isRefreshingCache) return;
        
        setIsRefreshingCache(true);
        try {
            const result = await storeService.refreshWooCommerceCache();
            if (result.success) {
                alert(`Cache refreshed successfully! ${result.productCount} products loaded.`);
            } else {
                alert('Failed to refresh cache. Please try again.');
            }
        } catch (error) {
            console.error('Error refreshing cache:', error);
            alert('Error refreshing cache. Please check the console for details.');
        } finally {
            setIsRefreshingCache(false);
        }
    };

    const getMembershipButtonText = () => {
        if (isCheckingMembership) {
            return 'LOADING...';
        }
        return auth.isAuthenticated && hasActiveMembership ? 'ALYZE+ APP' : 'ALYZE+';
    };

    const getMembershipButtonHref = () => {
        return auth.isAuthenticated && hasActiveMembership ? 'https://app.alyze.health' : '#';
    };

    return (
        <>
            <nav className={styles.navigation}>
                {/* Desktop Navigation */}
                <div className={styles.desktopNav}>
                    <div className={styles.navLeft}>
                        <Link to="/v1">
                            <div className={styles.logo}>
                                <img
                                    src={AlyzeWhiteLogo}
                                    alt="Alyze Health"
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                        </Link>
                    </div>

                    <div className={styles.navRight}>
                        <div className={styles.navIcons}>
                            {isAdmin && (
                                <button
                                    onClick={handleRefreshCache}
                                    disabled={isRefreshingCache}
                                    className={styles.viewCartButton}
                                    title="Refresh product cache from WooCommerce"
                                >
                                    {isRefreshingCache ? 'Refreshing...' : 'Refresh Cache'}
                                </button>
                            )}

                            <div className={styles.cartWrapper} ref={cartRef}>
                                <button onClick={toggleMiniCart} className={styles.cartButton}>
                                    <span>${totalAmount.toFixed(2)}</span>
                                    <svg className={styles.cartIcon} viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M323 292L323 207C329 95 391 33 501 33 610 33 673 95 677 209V292H750C796 292 833 329 833 375V875C833 921 796 958 750 958H250C204 958 167 921 167 875V375C167 329 204 292 250 292H323ZM385 292H615L615 210C611 130 577 95 501 95 425 95 390 130 385 209L385 292ZM323 354H250C238 354 229 363 229 375V875C229 887 238 896 250 896H750C762 896 771 887 771 875V375C771 363 762 354 750 354H677V454C677 471 663 485 646 485S615 471 615 454L615 354H385L385 454C385 471 371 485 354 485 337 485 323 471 323 454L323 354Z" fill="white" />
                                    </svg>
                                    {itemCount > 0 && (
                                        <span className={styles.cartCount}>{itemCount}</span>
                                    )}
                                </button>

                                {showMiniCart && (
                                    <div className={styles.miniCart}>
                                        <div className={styles.miniCartHeader}>
                                            <h3 className={styles.miniCartTitle}>Shopping Cart</h3>
                                            <button
                                                onClick={() => setShowMiniCart(false)}
                                                className={styles.closeButton}
                                            >
                                                <Dismiss24Regular />
                                            </button>
                                        </div>

                                        {items.length > 0 ? (
                                            <>
                                                <div className={styles.miniCartItems}>
                                                    {items.map((item, index) => (
                                                        <div key={`${item.productId}-${item.isSubscription}-${item.selectedInterval?.billingInterval || 'none'}-${item.selectedInterval?.billingPeriod || 'none'}-${index}`} className={styles.miniCartItem}>
                                                            <img
                                                                src={item.product?.imageUrl || '/default-product.jpg'}
                                                                alt={item.product?.name}
                                                                className={styles.itemImage}
                                                            />
                                                            <div className={styles.itemDetails}>
                                                                <h4 className={styles.itemName}>{item.product?.name}</h4>
                                                                <p className={styles.itemPrice}>
                                                                    Qty: {item.quantity} - ${(item.price * item.quantity).toFixed(2)}
                                                                    {item.isSubscription && item.selectedInterval && ` (Every ${item.selectedInterval.billingInterval} ${item.selectedInterval.billingPeriod}${(item.selectedInterval.billingInterval || 1) > 1 ? 's' : ''})`}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={() => handleRemoveItem(item.productId, item.isSubscription, item.selectedInterval)}
                                                                className={styles.removeButton}
                                                                data-tooltip={`Remove ${item.product?.name}`}
                                                                title={`Remove ${item.product?.name}`}
                                                            >
                                                                <Delete16Regular />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className={styles.miniCartFooter}>
                                                    <div className={styles.cartTotal}>
                                                        Total: ${totalAmount.toFixed(2)}
                                                    </div>
                                                    <button onClick={handleViewCart} className={styles.viewCartButton}>
                                                        View Cart
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className={styles.emptyCart}>
                                                No products in the cart.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <AlyzeUserLogin variant="desktop" />

                            <button onClick={toggleSidebar} style={{ backgroundColor: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '0', paddingTop: '5px' }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 18V16H21V18H3ZM3 13V11H21V13H3ZM3 8V6H21V8H3Z" fill="white" />
                                </svg>
                            </button>
                        </div>

                        <a
                            href={getMembershipButtonHref()}
                            target={auth.isAuthenticated && hasActiveMembership ? "_blank" : undefined}
                            rel={auth.isAuthenticated && hasActiveMembership ? "noopener noreferrer" : undefined}
                            className={styles.membershipButton}
                            onClick={handleMembershipButtonClick}
                        >
                            <div className={styles.membershipText}>{getMembershipButtonText()}</div>
                        </a>
                    </div>
                </div>

                {/* Mobile Navigation */}
                <div className={styles.mobileNav}>
                    <AlyzeUserLogin variant="mobile" />

                    <Link to="/v1" style={{paddingRight: '12px'}}>
                        <img
                            src={AlyzeWhiteLogo}
                            alt="Alyze Health"
                            style={{ height: '25px', width: 'auto' }}
                        />
                    </Link>

                    <button onClick={toggleSidebar} style={{ backgroundColor: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '0' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 18V16H21V18H3ZM3 13V11H21V13H3ZM3 8V6H21V8H3Z" fill="white" />
                        </svg>
                    </button>
                </div>
            </nav>

            {/* Sidebar */}
            <StoreNavSidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
        </>
    );
};
